import { useLingui } from "@lingui/react/macro";
import { ChevronRightIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { cn } from "@/utils/shadcn-ui";

/** A single entry in a navigation menu. Structure only — presentation is the
 * renderer's job, so the same definition can be styled differently per context
 * (desktop sidebar vs. mobile drawer). */
export interface MenuItem {
  to: string;
  /** Display-ready text: static titles arrive already translated. */
  label: string;
  /** Hide the item without removing it from the definition. Defaults to shown. */
  show?: boolean;
  /** Expand the children by default (only relevant when `children` is set). */
  defaultExpanded?: boolean;
  children?: MenuItem[];
}

export type MenuVariant = "sidebar" | "sheet";

/** Renders a menu definition for a given context. Recurses for nested items. */
export function MenuList({
  items,
  variant,
  level = 0,
  activeItem,
}: {
  items: MenuItem[];
  variant: MenuVariant;
  level?: number;
  activeItem?: MenuItem;
}) {
  const activePathname = useActivePathname();
  const visible = items.filter((item) => item.show !== false);
  if (visible.length === 0) {
    return null;
  }

  // Decide the single highlighted item ourselves (deepest path match) so a child
  // sharing its parent's `to` wins. Computed once at the root, then threaded down.
  const active = activeItem ?? findActiveItem(items, activePathname);

  return (
    <div className="flex flex-col">
      {visible.map((item) => (
        <MenuListItem
          key={item.to}
          item={item}
          variant={variant}
          level={level}
          activeItem={active}
        />
      ))}
    </div>
  );
}

function MenuListItem({
  item,
  variant,
  level,
  activeItem,
}: {
  item: MenuItem;
  variant: MenuVariant;
  level: number;
  activeItem?: MenuItem;
}) {
  const activePathname = useActivePathname();
  const { t } = useLingui();
  const childItems = (item.children ?? []).filter((c) => c.show !== false);
  const expandable = childItems.length > 0;
  const containsActiveRoute = expandable && isUnder(item.to, activePathname);
  const [expanded, setExpanded] = useState(
    item.defaultExpanded ?? containsActiveRoute,
  );

  // Auto-expand when the active route falls under this item. Not keyed on
  // `expanded`, so a manual collapse isn't immediately undone — it only
  // re-expands when the active route changes.
  useEffect(() => {
    if (containsActiveRoute) {
      setExpanded(true);
    }
  }, [containsActiveRoute]);

  const toggleExpanded = () => setExpanded((value) => !value);

  // Highlight the current item, or a collapsed ancestor that hides it.
  const isCurrent = item === activeItem;
  const selected = isCurrent || (containsActiveRoute && !expanded);

  // Top-level rows are emphasised only in the sheet, where they're the main nav;
  // in the sidebar they're a section's page items, so they stay regular weight.
  const isSheet = variant === "sheet";
  const top = level === 0 && isSheet;
  // The selected pill differs by variant: a faint grey on the drawer's white,
  // but white on the sidebar's grey (grey-on-grey wouldn't read).
  const selectedPill = isSheet ? "bg-[var(--menu-selected)]" : "bg-background";

  return (
    <>
      <div className="flex items-stretch pl-3 pr-2">
        {Array.from({ length: level }, (_, i) => (
          <div
            key={i}
            className="w-4 flex-none self-stretch border-l border-[var(--menu-rail)]"
          />
        ))}
        {/* The pill (hover/selected background) wraps the link and caret as
         * siblings — a <button> can't live inside an <a>. */}
        <div
          className={cn(
            "flex min-w-0 flex-1 items-center gap-2 rounded-md px-3",
            isSheet ? "hover:bg-accent" : "hover:bg-background",
            selected && selectedPill,
          )}
        >
          <Link
            to={item.to}
            aria-current={isCurrent ? "page" : undefined}
            className={cn(
              "min-w-0 flex-1 truncate py-1.5 text-base text-foreground hover:no-underline",
              top && "py-2.5",
              (top || selected) && "font-semibold",
            )}
          >
            {item.label}
          </Link>
          {expandable && (
            <button
              type="button"
              className="inline-flex flex-none cursor-pointer items-center justify-center border-none bg-transparent p-0.5 text-muted-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              onClick={toggleExpanded}
              aria-label={expanded ? t`Collapse` : t`Expand`}
            >
              <ChevronRightIcon
                className={`size-4 transition-transform ${
                  expanded ? "rotate-90" : ""
                }`}
              />
            </button>
          )}
        </div>
      </div>
      {expandable && expanded && (
        <MenuList
          items={childItems}
          variant={variant}
          level={level + 1}
          activeItem={activeItem}
        />
      )}
    </>
  );
}

/** Pathname used to decide the active menu item. Routes that live off the main
 * nav (e.g. a patient's Settings page) pass the tab they were opened from as
 * `location.state.settingsFrom`, so that origin tab stays highlighted instead
 * of the page collapsing onto its closest ancestor. Without it we use the URL. */
function useActivePathname(): string {
  const location = useLocation();
  const settingsFrom = (location.state as { settingsFrom?: string } | null)
    ?.settingsFrom;
  return settingsFrom ?? location.pathname;
}

/** The deepest item whose `to` matches the URL. Ties (equal depth) go to the
 * later-visited item, so a child sharing its parent's `to` (e.g. Profile vs. the
 * patient node) wins, since children are visited after their parent. */
function findActiveItem(
  items: MenuItem[],
  pathname: string,
): MenuItem | undefined {
  let best: MenuItem | undefined;
  const visit = (list: MenuItem[]) => {
    for (const item of list) {
      const matches =
        item.to === "/" ? pathname === "/" : isUnder(item.to, pathname);
      if (
        item.show !== false &&
        matches &&
        (best === undefined || depth(item.to) >= depth(best.to))
      ) {
        best = item;
      }
      if (item.children) {
        visit(item.children);
      }
    }
  };
  visit(items);
  return best;
}

/** Whether the URL is at or below `to` (root is an ancestor of everything). */
export function isUnder(to: string, pathname: string): boolean {
  return to === "/" || pathname === to || pathname.startsWith(`${to}/`);
}

/** Number of path segments in `to` (`/` is 0, `/patient/123` is 2). */
function depth(to: string): number {
  return to.split("/").filter(Boolean).length;
}
